/**
 * Shared "this tile could not be read" affordance.
 *
 * U29 (2026-08-21): every System Health read used to end in a catch that
 * returned an empty result, so an IAM denial or a CloudWatch outage rendered
 * as a calm, green, all-zero page. The backend now stamps each tile with
 * {status, error}; these helpers make the UI say so.
 *
 * The rule the cards follow: an unreadable tile shows an em-dash, never a
 * zero. A zero is a measurement. "Unavailable" is the absence of one, and an
 * ops page that cannot tell those apart is worse than no ops page.
 *
 * Amber, not red — an unread tile is an unknown, not a known failure.
 */
export interface TileMeta {
  status?: 'ok' | 'unavailable';
  error?: string | null;
}

export function isUnavailable(data?: TileMeta | null): boolean {
  return data?.status === 'unavailable';
}

/** Card border classes: amber when unavailable, otherwise the caller's own. */
export function tileShell(unavailable: boolean, whenOk: string): string {
  return unavailable ? 'border-amber-500/50 bg-amber-500/5' : whenOk;
}

export function TileUnavailable({ error }: { error?: string | null }) {
  return (
    <div className="rounded border border-amber-500/50 bg-amber-500/10 p-3 text-sm text-amber-800 mb-4">
      <div className="font-medium">Unavailable — this tile could not be read.</div>
      <div className="text-xs mt-0.5 opacity-90">
        The figures below are not a measurement. Do not read them as healthy.
      </div>
      {error && <div className="mt-1 font-mono text-xs break-all opacity-80">{error}</div>}
    </div>
  );
}
